/**
 * Central query-key factory.
 *
 * Every `useQuery` and every `invalidateQueries` call uses a key from here.
 * Keys are hierarchical, so a mutation can invalidate exactly the affected
 * subtree - `queryKeys.me.sessions.all` after a cancellation, not the whole
 * cache - which is what keeps the portals from refetching everything on every
 * action.
 */

export const queryKeys = {
  session: () => ['session'] as const,

  publicSite: {
    content: ['public', 'site'] as const,
    programs: ['public', 'programs'] as const,
    packages: ['public', 'packages'] as const,
    results: (filters?: unknown) => ['public', 'results', filters ?? null] as const,
    faqs: ['public', 'faqs'] as const,
    sessionTypes: ['public', 'session-types'] as const,
    locations: ['public', 'locations'] as const,
    availability: (query: unknown) => ['public', 'availability', query] as const,
  },

  me: {
    profile: ['me', 'profile'] as const,
    dashboard: ['me', 'dashboard'] as const,
    onboarding: ['me', 'onboarding'] as const,
    consents: ['me', 'consents'] as const,
    goals: ['me', 'goals'] as const,
    packages: ['me', 'packages'] as const,
    credits: ['me', 'credits'] as const,
    sessionTypes: ['me', 'session-types'] as const,
    availability: (query: unknown) => ['me', 'availability', query] as const,
    bookingRequests: {
      all: ['me', 'booking-requests'] as const,
      list: (query: unknown) => ['me', 'booking-requests', 'list', query] as const,
      detail: (id: string) => ['me', 'booking-requests', id] as const,
    },
    sessions: {
      all: ['me', 'sessions'] as const,
      list: (query: unknown) => ['me', 'sessions', 'list', query] as const,
      detail: (id: string) => ['me', 'sessions', id] as const,
    },
    calendar: (query: unknown) => ['me', 'calendar', query] as const,
    trainingPrograms: {
      all: ['me', 'training-programs'] as const,
      detail: (id: string) => ['me', 'training-programs', id] as const,
    },
    workouts: {
      all: ['me', 'workouts'] as const,
      list: (query: unknown) => ['me', 'workouts', 'list', query] as const,
      detail: (id: string) => ['me', 'workouts', id] as const,
    },
    nutritionPlans: {
      all: ['me', 'nutrition-plans'] as const,
      detail: (id: string) => ['me', 'nutrition-plans', id] as const,
    },
    nutritionLogs: (query: unknown) => ['me', 'nutrition-logs', query] as const,
    checkIns: {
      all: ['me', 'check-ins'] as const,
      list: (query: unknown) => ['me', 'check-ins', 'list', query] as const,
      detail: (id: string) => ['me', 'check-ins', id] as const,
    },
    forms: {
      all: ['me', 'forms'] as const,
      detail: (id: string) => ['me', 'forms', id] as const,
    },
    progress: (query: unknown) => ['me', 'progress', query] as const,
    analysis: (query: unknown) => ['me', 'analysis', query] as const,
    exerciseProgression: (exerciseId: string) =>
      ['me', 'progress', 'exercises', exerciseId] as const,
    measurements: (query: unknown) => ['me', 'measurements', query] as const,
    progressPhotos: (query: unknown) => ['me', 'progress-photos', query] as const,
    feedback: {
      all: ['me', 'feedback'] as const,
      list: (query: unknown) => ['me', 'feedback', 'list', query] as const,
    },
    conversations: {
      all: ['me', 'conversations'] as const,
      list: (query: unknown) => ['me', 'conversations', 'list', query] as const,
      messages: (conversationId: string, query?: unknown) =>
        ['me', 'conversations', conversationId, 'messages', query ?? null] as const,
    },
    documents: {
      all: ['me', 'documents'] as const,
      list: (query: unknown) => ['me', 'documents', 'list', query] as const,
    },
    notifications: {
      all: ['me', 'notifications'] as const,
      list: (query: unknown) => ['me', 'notifications', 'list', query] as const,
      preferences: ['me', 'notifications', 'preferences'] as const,
      unreadCounts: ['me', 'unread-counts'] as const,
    },
    pushSubscriptions: ['me', 'push-subscriptions'] as const,
    /** Same key as `notifications.unreadCounts`; the badge is read from both. */
    unreadCounts: ['me', 'unread-counts'] as const,
    sessionsDevices: ['me', 'devices'] as const,
  },

  admin: {
    dashboard: ['admin', 'dashboard'] as const,
    administrators: ['admin', 'administrators'] as const,
    calendar: (query: unknown) => ['admin', 'calendar', query] as const,
    /** Prefix for invalidating every calendar range at once after a schedule change. */
    calendarAll: ['admin', 'calendar'] as const,
    bookingRequests: {
      all: ['admin', 'booking-requests'] as const,
      list: (query: unknown) => ['admin', 'booking-requests', 'list', query] as const,
      detail: (id: string) => ['admin', 'booking-requests', id] as const,
    },
    sessions: {
      all: ['admin', 'sessions'] as const,
      list: (query: unknown) => ['admin', 'sessions', 'list', query] as const,
      detail: (id: string) => ['admin', 'sessions', id] as const,
    },
    clients: {
      all: ['admin', 'clients'] as const,
      list: (query: unknown) => ['admin', 'clients', 'list', query] as const,
      detail: (id: string) => ['admin', 'clients', id] as const,
      overview: (id: string) => ['admin', 'clients', id, 'overview'] as const,
      health: (id: string) => ['admin', 'clients', id, 'health'] as const,
      goals: (id: string) => ['admin', 'clients', id, 'goals'] as const,
      notes: (id: string) => ['admin', 'clients', id, 'notes'] as const,
      consents: (id: string) => ['admin', 'clients', id, 'consents'] as const,
      timeline: (id: string, query?: unknown) =>
        ['admin', 'clients', id, 'timeline', query ?? null] as const,
      credits: (id: string) => ['admin', 'clients', id, 'credits'] as const,
      packages: (id: string) => ['admin', 'clients', id, 'packages'] as const,
      progress: (id: string, query?: unknown) =>
        ['admin', 'clients', id, 'progress', query ?? null] as const,
      analysis: (id: string, query?: unknown) =>
        ['admin', 'clients', id, 'analysis', query ?? null] as const,
      progressPhotos: (id: string, query?: unknown) =>
        ['admin', 'clients', id, 'progress-photos', query ?? null] as const,
      workouts: (id: string, query?: unknown) =>
        ['admin', 'clients', id, 'workouts', query ?? null] as const,
      fitnessTests: (id: string) => ['admin', 'clients', id, 'fitness-tests'] as const,
    },
    coaches: ['admin', 'coaches'] as const,
    locations: ['admin', 'locations'] as const,
    exercises: {
      all: ['admin', 'exercises'] as const,
      list: (query: unknown) => ['admin', 'exercises', 'list', query] as const,
      detail: (id: string) => ['admin', 'exercises', id] as const,
    },
    trainingTemplates: ['admin', 'training-templates'] as const,
    trainingPrograms: {
      all: ['admin', 'training-programs'] as const,
      list: (query: unknown) => ['admin', 'training-programs', 'list', query] as const,
      detail: (id: string) => ['admin', 'training-programs', id] as const,
    },
    nutritionTemplates: ['admin', 'nutrition-templates'] as const,
    nutritionPlans: {
      all: ['admin', 'nutrition-plans'] as const,
      list: (query: unknown) => ['admin', 'nutrition-plans', 'list', query] as const,
      detail: (id: string) => ['admin', 'nutrition-plans', id] as const,
    },
    checkIns: {
      all: ['admin', 'check-ins'] as const,
      list: (query: unknown) => ['admin', 'check-ins', 'list', query] as const,
      detail: (id: string) => ['admin', 'check-ins', id] as const,
      reviewContext: (id: string) => ['admin', 'check-ins', id, 'review-context'] as const,
      templates: ['admin', 'check-in-templates'] as const,
    },
    forms: {
      templates: ['admin', 'form-templates'] as const,
      packs: ['admin', 'form-packs'] as const,
      assignments: {
        all: ['admin', 'form-assignments'] as const,
        list: (query: unknown) => ['admin', 'form-assignments', 'list', query] as const,
        detail: (id: string) => ['admin', 'form-assignments', id] as const,
      },
    },
    feedback: {
      all: ['admin', 'feedback'] as const,
      list: (query: unknown) => ['admin', 'feedback', 'list', query] as const,
      detail: (id: string) => ['admin', 'feedback', id] as const,
    },
    availability: {
      rules: (coachId?: string) => ['admin', 'availability', 'rules', coachId ?? null] as const,
      exceptions: (coachId?: string) =>
        ['admin', 'availability', 'exceptions', coachId ?? null] as const,
    },
    sessionTypes: ['admin', 'session-types'] as const,
    packages: {
      all: ['admin', 'packages'] as const,
      clientPackages: (query: unknown) => ['admin', 'client-packages', query] as const,
      clientPackagesAll: ['admin', 'client-packages'] as const,
      ledger: (query: unknown) => ['admin', 'credits', 'ledger', query] as const,
      ledgerAll: ['admin', 'credits'] as const,
    },
    conversations: {
      all: ['admin', 'conversations'] as const,
      list: (query: unknown) => ['admin', 'conversations', 'list', query] as const,
      messages: (conversationId: string, query?: unknown) =>
        ['admin', 'conversations', conversationId, 'messages', query ?? null] as const,
    },
    documents: {
      all: ['admin', 'documents'] as const,
      list: (query: unknown) => ['admin', 'documents', 'list', query] as const,
    },
    content: {
      all: ['admin', 'content'] as const,
      entries: ['admin', 'content', 'entries'] as const,
      readiness: ['admin', 'content', 'readiness'] as const,
    },
    media: {
      all: ['admin', 'media'] as const,
      list: (query: unknown) => ['admin', 'media', 'list', query] as const,
      modelAssets: ['admin', 'media', 'model-assets'] as const,
    },
    testimonials: {
      all: ['admin', 'testimonials'] as const,
      list: (query: unknown) => ['admin', 'testimonials', 'list', query] as const,
      results: (query: unknown) => ['admin', 'results', query] as const,
      resultsAll: ['admin', 'results'] as const,
    },
    leads: {
      all: ['admin', 'leads'] as const,
      list: (query: unknown) => ['admin', 'leads', 'list', query] as const,
    },
    reports: {
      all: ['admin', 'reports'] as const,
      definitions: ['admin', 'reports', 'definitions'] as const,
      result: (key: string, filters: unknown) => ['admin', 'reports', key, filters] as const,
    },
    notifications: {
      all: ['admin', 'notifications'] as const,
      mailStatus: ['admin', 'notifications', 'mail-status'] as const,
      mailboxAll: ['admin', 'notifications', 'mailbox'] as const,
      mailbox: (query: unknown) => ['admin', 'notifications', 'mailbox', query] as const,
      deliveries: (query: unknown) => ['admin', 'notifications', 'deliveries', query] as const,
      jobs: (query: unknown) => ['admin', 'notifications', 'jobs', query] as const,
      jobsAll: ['admin', 'notifications', 'jobs'] as const,
    },
    settings: ['admin', 'settings'] as const,
    audit: (query: unknown) => ['admin', 'audit', query] as const,
    externalCalendars: ['admin', 'external-calendars'] as const,
  },
} as const;
